import type { TableColumn } from "@kyan/ui";
import { cn } from "@kyan/ui";

import { getPerpConstraints } from "#/config/orders";
import { Market } from "#/types/markets";
import { type OrderbookEntry } from "#/types/orderbook";
import { formatCurrency, formatPercent } from "#/utils/valueFormatters";

import { SizeDisplay } from "../SizeDisplay/SizeDisplay";

export const getColumns = (
  market: Market,
  isOptions: boolean,
): TableColumn<OrderbookEntry>[] => [
  {
    id: "price",
    header: "Price",
    cell: (row) => (!isNaN(row.price) ? formatCurrency(row.price) : ""),
    columnClassName: cn({
      "sm:text-xxs py-1.5 pl-1": !isOptions,
      "py-1 pl-2": isOptions,
    }),
    cellClassName: cn({
      "pl-1": !isOptions,
      "pl-2": isOptions,
    }),
    minWidth: "80px",
  },
  ...(isOptions
    ? [
        {
          id: "iv",
          header: "IV",
          cell: (row) =>
            Number(row.iv) > 0
              ? formatPercent(row.iv ?? 0, {
                  precision: 2,
                  signDisplay: "never",
                })
              : "-",
          columnClassName: cn("ml-auto", {
            "sm:text-xxs py-0.5": !isOptions,
            "py-1": isOptions,
          }),
          cellClassName: "text-textPrimary ml-auto",
          minWidth: "40px",
          maxWidth: "100px",
        } as TableColumn<OrderbookEntry>,
      ]
    : []),
  {
    id: "size",
    header: "Size",
    cell: (row) => {
      if (isNaN(row.size) || !market) return "";

      const { sizeDisplayPrecision } = getPerpConstraints(market.underlying);

      return (
        <SizeDisplay
          value={row.size}
          underlying={market.underlying}
          precision={sizeDisplayPrecision}
        />
      );
    },
    columnClassName: cn("ml-auto", {
      "sm:text-xxs py-1.5": !isOptions,
      "py-1": isOptions,
    }),
    cellClassName: "text-textPrimary ml-auto",
    minWidth: "80px",
    maxWidth: "120px",
  },
  {
    id: "total",
    header: "Total",
    cell: (row) => {
      if (isNaN(row.total)) return "";

      const { sizeDisplayPrecision } = getPerpConstraints(market.underlying);

      return (
        <SizeDisplay
          value={row.total}
          underlying={market.underlying}
          precision={sizeDisplayPrecision}
        />
      );
    },
    columnClassName: cn("ml-auto", {
      "sm:text-xxs py-1.5 pr-2": !isOptions,
      "py-1 pr-3": isOptions,
    }),
    cellClassName: cn("text-textPrimary ml-auto", {
      "pr-2": !isOptions,
      "pr-3": isOptions,
    }),
    minWidth: "80px",
    maxWidth: "120px",
  },
];
